import React, { useState, useContext, useEffect, useRef } from "react";
import { Route, Switch } from "react-router-dom";
import {
  ReferrerDetector,
  ScrollRestoration,
  PhraseAppToolbar,
  StateValidator,
  YearSwitchBanner,
  Footer,
  GlobalSessionHandler,
} from "../components";
import {
  InjectIntlContext,
  cookiesAddedOrRemoved,
  getLdUser,
  useYearSwitchBanner,
} from "../helpers";
import * as Pages from "../pages";
import MaintenancePage from "../components/MaintenancePage";
import { IntlProvider, addLocaleData } from "react-intl";
import messages_en from "../translations/en-US.json";
import messages_es from "../translations/es-MX.json";
import Routes from "./routes";
import { useFlags, useLDClient } from "launchdarkly-react-client-sdk";
import { AppStore } from "./store";
import en from "react-intl/locale-data/en";
import es from "react-intl/locale-data/es";
import RouteToastHandler from "../components/RouteToastHandler";
import UserActivityMonitor from "../components/UserActivityMonitor";
import MaxGlobalHeader from "./MaxGlobalHeader";

const AppContent: React.FunctionComponent = () => {
  const flags = useFlags();
  const { state } = useContext(AppStore.AppContext);

  const {
    fePhraseappToolbar,
    feMaintenanceMode,
    feEnableAuthentication,
    tmpFeEnableMaxGlobalHeader,
    tmpUseSlsxSessionHeader,
  } = flags;

  const showYearSwitchBanner = useYearSwitchBanner();

  // The IntlProvider adds language localization to its children with a default language and traslations map as props
  const messages = {
    en: messages_en,
    es: messages_es,
  };

  addLocaleData([...en, ...es]);

  const [
    refreshComponentsForTranslation,
    setRefreshComponentsForTranslation,
  ] = useState(false);

  /**
   * Get the LaunchDarkly client.
   * Provide a callback for updating the user flags if a polled cookie changes.
   */
  const ldClient = useLDClient();

  const updateLdUser = () => {
    const ldUser = getLdUser(state);
    ldClient?.identify(ldUser, undefined, err => {
      if (err) {
        console.error(err);
      }
    });
  };

  const [ldFlagTimeout, setLdFlagTimeout] = useState<
    NodeJS.Timeout | undefined
  >();
  const [couldNotRetrieveLdFlags, setCouldNotRetrieveLdFlags] = useState(false);
  const [ldFlagsAvailable, setLdFlagsAvailable] = useState(false);

  useEffect(() => {
    const ldFlagsAreAvailable = Object.getOwnPropertyNames(flags).length > 0;

    setLdFlagsAvailable(ldFlagsAreAvailable);

    if (!ldFlagsAreAvailable) {
      setLdFlagTimeout(
        setTimeout(() => {
          setCouldNotRetrieveLdFlags(true);
        }, 5000)
      );
    } else {
      if (ldFlagTimeout) {
        clearTimeout(ldFlagTimeout);
        setLdFlagTimeout(undefined);
      }
      setCouldNotRetrieveLdFlags(false);
    }
  }, [flags]);

  /**
   * Watch for an update to cookies and confirm flags have been
   * retrived.
   * If the specified cookie (key) has been added or removed, update flags,
   * accordingly.
   */
  const useCookieChanged = (key: string) => {
    const ref = useRef(document.cookie);
    useEffect(() => {
      if (couldNotRetrieveLdFlags) {
        return;
      }
      const cookiesChanged = cookiesAddedOrRemoved(key, ref.current)();
      if (cookiesChanged) {
        updateLdUser();
      }
      ref.current = document.cookie;
    }, [document.cookie, couldNotRetrieveLdFlags]);
    return ref.current;
  };

  useCookieChanged("sid");

  /**
   * Watch for an update to `state.beneficiary` and confirm flags have been
   * retrived.
   * If beneficiary data is available, update flags, accordingly.
   */
  useEffect(() => {
    if (couldNotRetrieveLdFlags || !state.beneficiary) {
      return;
    }
    updateLdUser();
  }, [couldNotRetrieveLdFlags, state.beneficiary]);

  // @TODO - This code to be removed when `tmp-use-slsx-session-header` is retired,
  // along with the flag. Replace `WrappingSessionHandlerComponent` directly  with
  // `GlobalSessionHandler`
  const WrappingSessionHandlerComponent = tmpUseSlsxSessionHeader
    ? GlobalSessionHandler
    : React.Fragment;

  return (
    <IntlProvider
      locale={state.language}
      messages={messages[state.language] as Record<string, unknown>}
    >
      <InjectIntlContext>
        <MaxGlobalHeader>
          <StateValidator>
            <ScrollRestoration>
              <ReferrerDetector>
                <UserActivityMonitor>
                  <RouteToastHandler>
                    <div className="ds-base">
                      {fePhraseappToolbar && (
                        <PhraseAppToolbar
                          onTranslationEnabled={(): void => {
                            // This method will hide components and force them to re-render so that PhraseApp can read the translation keys
                            setRefreshComponentsForTranslation(true);

                            setTimeout(() =>
                              setRefreshComponentsForTranslation(false)
                            );
                          }}
                        />
                      )}

                      {showYearSwitchBanner && <YearSwitchBanner />}

                      {!refreshComponentsForTranslation &&
                      (ldFlagsAvailable || couldNotRetrieveLdFlags) ? (
                        <div className="AppContent">
                          <main className="app__main">
                            {feMaintenanceMode || couldNotRetrieveLdFlags ? (
                              <MaintenancePage />
                            ) : (
                              // @TODO - When `tmp-use-slsx-session-header` is retired,
                              // replace `WrappingSessionHandlerComponent` directly  with
                              // `GlobalSessionHandler`. Also possible it could be moved
                              // up in the component tree to below `UserActivityMonitor`
                              <WrappingSessionHandlerComponent>
                                <Switch>
                                  <Route
                                    path={Routes.searchResults}
                                    component={Pages.SearchResultsPage}
                                  />
                                  <Route
                                    path={Routes.sanctionedPlans}
                                    component={Pages.SanctionedPlansPage}
                                  />
                                  <Route
                                    path={Routes.drugSearchPreferences}
                                    component={Pages.DrugSearchPreferencesPage}
                                  />
                                  <Route
                                    path={Routes.comparePlans}
                                    component={Pages.PlanComparePage}
                                  />
                                  <Route
                                    path={Routes.planDetails}
                                    component={Pages.PlanDetailsPage}
                                  />
                                  <Route
                                    path={Routes.managePrescriptions}
                                    component={Pages.ManagePrescriptionsPage}
                                  />
                                  <Route
                                    path={Routes.prescriptionsList}
                                    component={Pages.ManagePrescriptionsPage}
                                  />
                                  <Route
                                    path={Routes.managePharmacies}
                                    component={Pages.ManagePharmaciesPage}
                                  />
                                  <Route
                                    path={Routes.inNetworkPharmacy}
                                    exact
                                    render={props => (
                                      <Pages.SelectPharmaciesPage
                                        isInNetwork={true}
                                        {...props}
                                      />
                                    )}
                                  />
                                  <Route
                                    path={Routes.pharmacy}
                                    component={Pages.SelectPharmaciesPage}
                                  />
                                  <Route
                                    path={Routes.coverageSelection}
                                    component={Pages.CoverageSelectionPage}
                                  />
                                  {!tmpUseSlsxSessionHeader && (
                                    <Route
                                      path={Routes.slsCallback}
                                      component={Pages.LoginCallbackHandler}
                                    />
                                  )}
                                  {feEnableAuthentication && (
                                    <Route
                                      path={Routes.beneLanding}
                                      component={Pages.BeneLandingPage}
                                    />
                                  )}
                                  <Route
                                    path={Routes.questionRouting}
                                    component={Pages.QuestionRoutingPage}
                                  />
                                  <Route
                                    path={Routes.enroll}
                                    component={Pages.EnrollmentFormPage}
                                  />
                                  <Route
                                    path={Routes.csrLanding}
                                    component={Pages.CSRLandingPage}
                                  />
                                  <Route
                                    path={Routes.pdePage}
                                    component={Pages.PDEPage}
                                  />
                                  {!tmpUseSlsxSessionHeader && (
                                    <Route
                                      path={Routes.mbpLandingPage}
                                      component={Pages.MBPLandingPage}
                                    />
                                  )}
                                  <Route
                                    path={Routes.version}
                                    component={Pages.VersionPage}
                                  />
                                  <Route
                                    path={Routes.planPreview}
                                    component={Pages.PlanPreviewPage}
                                  />
                                  <Route
                                    path={Routes.coverageWizard.options}
                                    component={Pages.CoverageWizardOptionsPage}
                                  />
                                  <Route
                                    path={Routes.coverageWizard.info}
                                    component={Pages.CoverageWizardInfoPage}
                                  />
                                  <Route
                                    path={Routes.coverageWizard.landingPage}
                                    component={Pages.CoverageWizardLandingPage}
                                  />
                                  <Route
                                    path={Routes.pace.plans}
                                    component={Pages.PacePlansPage}
                                  />
                                  <Route
                                    path={Routes.pace.landingPage}
                                    component={Pages.PaceLandingPage}
                                  />
                                  <Route
                                    path={Routes.pap.plans}
                                    component={Pages.PapPlansPage}
                                  />
                                  <Route
                                    path={Routes.spap.plans}
                                    component={Pages.SpapPlansPage}
                                  />
                                  <Route
                                    path={Routes.spap.landingPage}
                                    component={Pages.SpapLandingPage}
                                  />
                                  <Route
                                    path={Routes.pap.landingPage}
                                    component={Pages.PapLandingPage}
                                  />
                                  {tmpFeEnableMaxGlobalHeader && (
                                    <Route
                                      path={Routes.logout}
                                      component={Pages.LogoutPage}
                                    />
                                  )}
                                  <Route
                                    path={Routes.medigap.plans}
                                    component={Pages.MedigapPlans}
                                  />
                                  <Route
                                    path={Routes.medigap.planDetails}
                                    component={Pages.MedigapPlanDetailsPage}
                                  />
                                  <Route
                                    path={Routes.medigap.policies}
                                    component={Pages.MedigapPoliciesPage}
                                  />
                                  <Route
                                    path={Routes.medigap.landingPage}
                                    component={Pages.MedigapLandingPage}
                                  />
                                  <Route component={Pages.LandingPage} />
                                </Switch>
                              </WrappingSessionHandlerComponent>
                            )}
                          </main>

                          {!location.hash.includes(
                            Routes.planPreview.replace("/:id", "")
                          ) && <Footer />}
                        </div>
                      ) : (
                        <Pages.LoadingPage />
                      )}
                    </div>
                  </RouteToastHandler>
                </UserActivityMonitor>
              </ReferrerDetector>
            </ScrollRestoration>
          </StateValidator>
        </MaxGlobalHeader>
      </InjectIntlContext>
    </IntlProvider>
  );
};

export default AppContent;
